/**
 * Authentication Context
 *
 * This context provides authentication state and methods throughout the application.
 * It handles:
 * - Storing/retrieving JWT token from localStorage
 * - Fetching current user on app load
 * - Login/logout functionality
 * - Making auth state available to all components
 *
 * Usage in components:
 *   const { user, company, login, logout, loading } = useAuth();
 */

import { createContext, useContext, useState, useEffect } from 'react';
import * as authApi from '../api/authApi';

// Create the context with a default value of null
// This will be overwritten by the AuthProvider
const AuthContext = createContext(null);

/**
 * Custom hook to access authentication context
 *
 * This hook provides a convenient way to access auth state in any component.
 * Throws an error if used outside of AuthProvider.
 *
 * @returns {Object} Auth context value (user, company, login, logout, loading)
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Authentication Provider Component
 *
 * Wraps the application and provides authentication state to all children.
 * On mount, checks for existing token and fetches user data.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export function AuthProvider({ children }) {
  // Current authenticated user (null if not logged in)
  const [user, setUser] = useState(null);

  // Current user's company
  const [company, setCompany] = useState(null);

  // Loading state while checking authentication
  const [loading, setLoading] = useState(true);

  /**
   * Check for existing authentication on app load
   *
   * If a token exists in localStorage, try to fetch the current user.
   * This allows users to stay logged in across page refreshes.
   */
  useEffect(() => {
    const initAuth = async () => {
      // Check if token exists in localStorage
      const token = localStorage.getItem('token');

      if (token) {
        try {
          // Try to get current user with the stored token
          const response = await authApi.getMe();
          setUser(response.data.user);
          setCompany(response.data.company);
        } catch (error) {
          // Token is invalid or expired, clear it
          console.error('Failed to restore auth session:', error);
          localStorage.removeItem('token');
        }
      }

      // Done checking, set loading to false
      setLoading(false);
    };

    initAuth();
  }, []);

  /**
   * Log in a user
   *
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @returns {Promise} Resolves on success, rejects on failure
   */
  const login = async (email, password) => {
    // Call login API
    const response = await authApi.login(email, password);

    // Store token in localStorage for persistence
    localStorage.setItem('token', response.data.token);

    // Update state with user and company data
    setUser(response.data.user);
    setCompany(response.data.company);

    return response;
  };

  /**
   * Register a new company and admin user
   *
   * @param {Object} data - Registration data
   * @param {string} data.companyName - Name of the company
   * @param {string} data.name - User's name
   * @param {string} data.email - User's email
   * @param {string} data.password - User's password
   * @returns {Promise} Resolves on success, rejects on failure
   */
  const register = async (data) => {
    // Call register API
    const response = await authApi.register(data);

    // Store token in localStorage
    localStorage.setItem('token', response.data.token);

    // Update state with user and company data
    setUser(response.data.user);
    setCompany(response.data.company);

    return response;
  };

  /**
   * Log out the current user
   *
   * Clears the token from localStorage and resets state.
   */
  const logout = () => {
    // Remove token from localStorage
    localStorage.removeItem('token');

    // Clear state
    setUser(null);
    setCompany(null);
  };

  // Value object that will be provided to all consumers
  const value = {
    user,       // Current user object or null
    company,    // Current company object or null
    loading,    // True while checking initial auth state
    login,      // Function to log in
    register,   // Function to register
    logout,     // Function to log out
    setCompany  // Function to update company (for admin settings)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
